"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.View = void 0;
const commands_1 = require("../../commands");
const store_1 = require("../../exports/store");
const lodash_1 = require("lodash");
const model_1 = require("../../model");
const field_1 = require("./field");
const record_1 = require("./record");
class View {
    /**
     * Create a `View` instance from `IViewInfo`.
     *
     * @internal This constructor is not intended for public use.
     */
    constructor(datasheet, store, info) {
        this.datasheet = datasheet;
        this.store = store;
        const { property, fieldMap } = info;
        this.property = property;
        this.fieldMap = fieldMap;
    }
    get id() {
        return this.property.id;
    }
    get name() {
        return this.property.name;
    }
    get type() {
        return this.property.type;
    }
    get rows() {
        return this.property.rows;
    }
    get columns() {
        return this.property.columns;
    }
    /**
     * get view index, begin with 0.
     *
     * @return index of view
     */
    get index() {
        return store_1.Selectors.getViewIndex(this.datasheet.snapshot, this.id);
    }
    /**
     * Get all fields in the view. Hidden fields are not included by default.
     */
    getFields(options) {
        const { columns, fieldMap } = this;
        const { includeHidden } = options;
        const fields = [];
        for (const column of columns) {
            const field = fieldMap[column.fieldId];
            // When specifying a view, it is consistent with the view order being visible and invisible.
            if (field && (includeHidden || !column.hidden)) {
                fields.push(new field_1.Field(field, this.store)); // core.Field.bindContext(field, state).getApiMeta(dstId)));
            }
        }
        return Promise.resolve(fields);
    }
    /**
     * Get records in the view.
     */
    getRecords(options) {
        const { store, fieldMap } = this;
        const snapshot = store_1.Selectors.getSnapshot(store.getState());
        if (!snapshot) {
            return Promise.resolve([]);
        }
        const { pagination, maxRecords } = options;
        // Pagination
        let pageRows = this.rows;
        if (maxRecords !== undefined && maxRecords < this.rows.length) {
            pageRows = this.rows.slice(0, maxRecords);
        }
        if (pagination !== undefined) {
            const start = (pagination.pageNum - 1) * pagination.pageSize;
            const end = start + pagination.pageSize;
            pageRows = pagination.pageSize == -1 ? pageRows : pageRows.slice(start, end);
        }
        if (pageRows.length === 0) {
            return Promise.resolve([]);
        }
        const recordMap = snapshot.recordMap;
        const fieldKeys = Object.keys(this.fieldMap);
        const columnMap = (0, lodash_1.keyBy)(this.columns, 'fieldId');
        const voTransformOptions = {
            fieldMap,
            store,
            fieldKeys,
            columnMap,
        };
        const records = [];
        for (const row of pageRows) {
            if (recordMap[row.recordId]) {
                records.push(new record_1.Record(recordMap[row.recordId], {
                    voTransformOptions,
                }));
            }
        }
        return Promise.resolve(records);
    }
    /**
     * Add records to the datasheet via this view.
     *
     * @param options Options for adding records.
     * @param saveOptions Options for the data saver.
     * @return If the command execution succeeded, the `data` field of the return value is an array of record IDs.
     */
    addRecords(recordOptions, saveOptions) {
        return this.datasheet.addRecords(Object.assign(Object.assign({}, recordOptions), { viewId: this.id }), saveOptions);
    }
    /**
     * Delete this view.
     *
     * @param saveOptions The options that will be passed to the data saver.
     */
    delete(saveOptions) {
        return this.datasheet.deleteViews([this.id], saveOptions);
    }
    /**
     * Modify view property.
     *
     * @param view view info
     * @param saveOptions The options that will be passed to the data saver.
     */
    modify(view, saveOptions) {
        return this.datasheet.modifyViews([Object.assign(Object.assign({}, view), { viewId: this.id })], saveOptions);
    }
    /**
     * move view.
     *
     * @param view view move info
     * @param saveOptions The options that will be passed to the data saver.
     */
    move(view, saveOptions) {
        return this.datasheet.moveViews([Object.assign(Object.assign({}, view), { viewId: this.id })], saveOptions);
    }
    /**
     * Set lock info of the view.
     *
     * @param saveOptions The options that will be passed to the data saver.
     */
    setLockInfo(lockInfo, saveOptions) {
        return this.datasheet.doCommand({
            cmd: commands_1.CollaCommandName.SetViewLockInfo,
            data: lockInfo,
            viewId: this.id,
        }, saveOptions);
    }
    /**
     * Set autoSave of the view.
     *
     * @param saveOptions The options that will be passed to the data saver.
     */
    setAutoSave(autoSave, saveOptions) {
        return this.datasheet.doCommand({
            cmd: commands_1.CollaCommandName.SetViewAutoSave,
            autoSave,
            viewId: this.id,
        }, saveOptions);
    }
    /**
     * generate default view property
     *
     * @param viewType view type, if omitted, defaults to the type of this view.
     */
    deriveDefaultViewProperty(viewType) {
        return (0, model_1.getViewClass)(viewType !== null && viewType !== void 0 ? viewType : this.type).generateDefaultProperty(this.datasheet.snapshot, this.id, this.store.getState());
    }
}
exports.View = View;
//# sourceMappingURL=view.js.map